import { ContentTableItem } from "src/components/ContentTable/ContentTable";

function safeTime(iso: string | null): number {
	if (!iso) return -Infinity;
	const time = new Date(iso).getTime();
	return Number.isFinite(time) ? time : -Infinity;
}

// The form hides the `title` field when the user picks "Push
// notification" (only `pushTitle` is captured), so push-only rows
// arrive with an empty `title`. Fall back to `pushTitle` so the
// Content name column always reads something meaningful — and if
// both are somehow empty, use a clear placeholder rather than
// rendering a blank cell (which looks like a bug to users).
function displayName(announcement: MeeAnnouncement): string {
	const trimmedTitle = announcement.title?.trim() ?? "";
	if (trimmedTitle) return trimmedTitle;
	const trimmedPushTitle = announcement.pushTitle?.trim() ?? "";
	if (trimmedPushTitle) return trimmedPushTitle;
	return "Untitled";
}

export type NotificationType = "announcement" | "push";

/**
 * Infer whether an announcement row was authored as a full in-app
 * Announcement or as a Push-notification-only. The form derives this
 * from whether `body` is set (see `announcementToFormValues`); we
 * additionally treat any persisted content blocks as evidence of a
 * full Announcement so future content-block-only rows classify
 * correctly even if `body` ends up empty.
 */
export function getNotificationType(
	announcement: MeeAnnouncement
): NotificationType {
	if (announcement.body && announcement.body.trim().length > 0) {
		return "announcement";
	}
	if (announcement.contentBlocks && announcement.contentBlocks.length > 0) {
		return "announcement";
	}
	return "push";
}

export function mapAnnouncementsToContentTableItems(
	announcements: MeeAnnouncement[]
): ContentTableItem[] {
	const sent = announcements.filter((a) => a.status === "sent");
	const scheduled = announcements.filter((a) => a.status === "scheduled");
	const draft = announcements.filter((a) => a.status === "draft");
	const expired = announcements.filter((a) => a.status === "expired");

	const sortedSent = [...sent].sort(
		(a, b) => safeTime(b.sentAt) - safeTime(a.sentAt)
	);
	const sortedScheduled = [...scheduled].sort(
		(a, b) => safeTime(a.scheduledAt) - safeTime(b.scheduledAt)
	);
	// Sort draft rows alphabetically by whatever we'll display, so the
	// push-only rows (which have no `title`) don't all clump at the top.
	const sortedDraft = [...draft].sort((a, b) =>
		displayName(a).localeCompare(displayName(b))
	);
	const sortedExpired = [...expired].sort(
		(a, b) => safeTime(b.expiresAt) - safeTime(a.expiresAt)
	);

	const toItem = (
		announcement: MeeAnnouncement,
		isActive: boolean
	): ContentTableItem => ({
		id: announcement.id,
		name: displayName(announcement),
		createdBy: announcement.createdBy
			? `${announcement.createdBy.firstName} ${announcement.createdBy.lastName}`
			: undefined,
		lastUpdated: announcement.updatedAt ?? announcement.createdAt,
		isActive,
		data: {
			status: announcement.status,
			notificationType: getNotificationType(announcement),
			audienceType: announcement.audienceType,
			audiencePlatform: announcement.audiencePlatform,
			audienceUserIds: announcement.audienceUserIds,
			audienceGroupIds: announcement.audienceGroupIds,
			sentAt: announcement.sentAt,
			scheduledAt: announcement.scheduledAt,
			expiresAt: announcement.expiresAt
		}
	});

	return [
		...sortedSent.map((a) => toItem(a, true)),
		...sortedScheduled.map((a) => toItem(a, true)),
		...sortedDraft.map((a) => toItem(a, false)),
		...sortedExpired.map((a) => toItem(a, false))
	];
}
